import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useMetaTags } from '@/hooks/useMetaTags';
import { useMessages } from '@/hooks/useMessages';
import { useTranslation } from '@/hooks/useTranslation';
import PageHeader from '@/components/PageHeader';
import MessagePostIt from '@/components/MessagePostIt';
import MessageComposer from '@/components/MessageComposer';
import { Button } from '@/components/ui/button';
import { MessageSquareMore, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const Mensajes = () => {
  const { user } = useAuth();
  const { isAdmin, isSuperadmin } = useAdminAuth();
  const { t } = useTranslation();
  useMetaTags({ pathname: '/mensajes', title: t('guestbookTitle') });

  const {
    messages,
    loading,
    addMessage,
    editMessage,
    deleteMessage,
    canEdit,
    canDelete,
  } = useMessages();

  const [composerOpen, setComposerOpen] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleComposerClose = () => {
    setComposerOpen(false);
    setEditingMessageId(null);
  };

  const handleComposerSubmit = async (messageText: string) => {
    if (!user) return;

    setIsSaving(true);
    try {
      if (editingMessageId) {
        await editMessage(editingMessageId, messageText);

      } else {
        await addMessage(messageText);

      }
      handleComposerClose();
    } catch (error) {
      toast({
        title: t('error'),
        description: t('messageSaveError'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteMessage(messageId);
    } catch (error) {
      toast({
        title: t('error'),
        description: t('messageDeleteError'),
        variant: 'destructive',
      });
    }
  };

  const handleEditMessage = (messageId: string) => {
    const messageToEdit = messages.find((msg) => msg.id === messageId);
    if (messageToEdit) {
      setEditingMessageId(messageId);
      setComposerOpen(true);
    }
  };

  const editingMessage = editingMessageId
    ? messages.find((msg) => msg.id === editingMessageId)
    : null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageHeader title="Libro de Visitas" showUserActions={true} />

      {/* Main content */}
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-lg text-muted-foreground mb-4">
                {t('noMessagesYet')}
              </p>
            </div>
          ) : (
            <div className="space-y-2 message-list grid md:grid-cols-2">
              {messages.map((message) => {
                const isMessageAuthor = user?.id === message.user_id;

                // Determine which icons to show based on role rules
                const canShowEdit = isMessageAuthor || isSuperadmin;
                const canShowDelete = isMessageAuthor || isSuperadmin || isAdmin;

                return (
                  <MessagePostIt
                    key={message.id}
                    id={message.id}
                    message={message.message}
                    authorName={message.author_name || 'Invitado'}
                    authorAvatar={message.author_avatar}
                    dateCreated={message.created_at}
                    isOwnMessage={isMessageAuthor}
                    onEdit={canShowEdit && canEdit(message.user_id) ? () => handleEditMessage(message.id) : undefined}
                    onDelete={canShowDelete && canDelete(message.user_id) ? () => handleDeleteMessage(message.id) : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Fixed input button at bottom */}
      {user && (
        <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3 flex justify-center">
          <Button
            onClick={() => setComposerOpen(true)}
            size="lg"
            className="w-full max-w-md h-14 rounded-full gap-2 text-lg font-semibold"
            style={{
              fontSize: '1.125rem',
            }}
          >
            <MessageSquareMore className="w-5 h-5" />
            {t('writeMessage')}
          </Button>
        </div>
      )}

      {/* Message Composer Modal */}
      <MessageComposer
        isOpen={composerOpen}
        onClose={handleComposerClose}
        onSubmit={handleComposerSubmit}
        initialMessage={editingMessage?.message || ''}
        isEditing={!!editingMessageId}
        isSaving={isSaving}
      />
    </div>
  );
};

export default Mensajes;
